import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { EyeOff, LayoutDashboard, Settings2 } from 'lucide-react';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useAuth } from '@/hooks/useAuth';
import { CRM_PAGES, pageForPath } from '@/lib/crm-pages';

/**
 * Stops a page the company's blueprint (or its admin) turned off from opening
 * through a bookmark or an old link. Until the workspace answers, everything
 * renders — the guard never blocks on a slow request.
 */
export const PageGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { pathname } = useLocation();
  const { info, pageOn } = useWorkspace();
  const { user } = useAuth();
  const page = pageForPath(pathname);
  if (!info || !page || pageOn(page)) return <>{children}</>;

  const def = CRM_PAGES.find((p) => p.key === page);
  const hiddenByAdmin = info.availablePages?.[page] === true;
  return (
    <div className="flex min-h-full items-center justify-center px-6 py-16">
      <div className="w-full max-w-md rounded-2xl border border-border bg-surface p-8 text-center shadow-soft">
        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-surface-alt text-text-muted">
          <EyeOff size={22} />
        </span>
        <h1 className="mt-4 font-display text-lg font-semibold text-text-primary">
          {def?.label ?? 'This page'} isn't in your workspace
        </h1>
        <p className="mt-1.5 text-sm text-text-muted">
          {hiddenByAdmin
            ? 'Your admin hid it from the sidebar. It can be switched back on in Settings.'
            : `It isn't part of the ${info.family?.name ?? 'standard'} setup for ${info.companyName}.`}
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <Link to="/dashboard" className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-primary px-4 text-sm font-semibold text-white hover:opacity-90">
            <LayoutDashboard size={15} /> Go to dashboard
          </Link>
          {hiddenByAdmin && user?.role === 'company_admin' && (
            <Link to="/settings/industry" className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-border px-4 text-sm font-medium text-text-primary hover:bg-surface-alt">
              <Settings2 size={15} /> Industry & pages
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};
